/* eslint-disable @atlassian/perf-linting/no-quadratic-complexity -- bounded traversal of the query's selections */
import type { DataProxy } from 'apollo-cache';
import type { OperationVariables } from '@apollo/react-common';
import {
	createOperationDescriptor,
	getRequest,
	getStorageKey,
	type NormalizationSelection,
	type RecordProxy,
	type RecordSourceProxy,
	type ReaderSelection,
} from 'relay-runtime';

import type { ReadQueryInteropArgs } from './readQuery.ts';
import type { WriteQueryInteropArgs } from './writeQuery.ts';
import { cloneRelayQueryResult } from './cloneRelayQueryResult.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import { writeQueryPayloadToStore } from './relayStorePayloadWrite.ts';

/** The query operations supported by legacy mutation update callbacks. */
export type MutationQueryCache = {
	readQuery<TData, TVariables extends OperationVariables = OperationVariables>(
		options: ReadQueryInteropArgs<TVariables>,
	): TData | null;
	writeQuery<TData, TVariables extends OperationVariables = OperationVariables>(
		options: WriteQueryInteropArgs<TData, TVariables>,
	): void;
};

export function createApolloMutationQueryCache(cache: DataProxy): MutationQueryCache {
	return {
		readQuery: (options) => cache.readQuery({ ...options, query: toDocumentNode(options.query) }),
		writeQuery: (options) => cache.writeQuery({ ...options, query: toDocumentNode(options.query) }),
	};
}

export function createRelayMutationQueryCache(store: RecordSourceProxy): MutationQueryCache {
	return {
		readQuery: <TData, TVariables extends OperationVariables = OperationVariables>(
			options: ReadQueryInteropArgs<TVariables>,
		): TData | null => {
			const operation = createOperationDescriptor(
				getRequest(options.query),
				options.variables ?? {},
			);
			let missing = false;
			// Read the transaction proxy, not the published environment: this sees optimistic
			// mutation fields and preceding writes from the same update callback.
			const read = (
				record: RecordProxy | null | undefined,
				selections: readonly NormalizationSelection[],
			): Record<string, unknown> | null | undefined => {
				if (record == null) {
					missing ||= record === undefined;
					return record;
				}
				const data: Record<string, unknown> = {};
				for (const selection of selections) {
					switch (selection.kind) {
						case 'ScalarField': {
							const value = record.getValue(getStorageKey(selection, operation.request.variables));
							missing ||= value === undefined;
							data[selection.alias ?? selection.name] = value;
							break;
						}
						case 'LinkedField': {
							const key = getStorageKey(selection, operation.request.variables);
							const records = selection.plural ? record.getLinkedRecords(key) : undefined;
							const value = selection.plural
								? records == null
									? records
									: records.map((child) => read(child, selection.selections))
								: read(record.getLinkedRecord(key), selection.selections);
							missing ||= value === undefined;
							data[selection.alias ?? selection.name] = value;
							break;
						}
						case 'Condition':
							if (operation.request.variables[selection.condition] === selection.passingValue) {
								Object.assign(data, read(record, selection.selections));
							}
							break;
						case 'InlineFragment':
							if (
								selection.abstractKey != null
									? store
											.get(`client:__type:${record.getType()}`)
											?.getValue(selection.abstractKey) === true
									: record.getType() === selection.type
							) {
								Object.assign(data, read(record, selection.selections));
							}
							break;
						case 'ClientExtension':
						case 'Defer':
						case 'Stream':
							Object.assign(data, read(record, selection.selections));
							break;
						case 'TypeDiscriminator':
						case 'ScalarHandle':
						case 'LinkedHandle':
							break;
						default:
							throw new Error(`Unsupported mutation cache query selection: ${selection.kind}`);
					}
				}
				return data;
			};
			const data = read(store.getRoot(), operation.root.node.selections);
			return missing ? null : cloneRelayQueryResult(data as TData);
		},
		writeQuery: (options) => {
			const operation = createOperationDescriptor(
				getRequest(options.query),
				options.variables ?? {},
			);
			writeQueryPayloadToStore(
				store,
				operation.root.node.selections as readonly ReaderSelection[],
				options.data as Record<string, unknown>,
				operation.request.variables,
			);
		},
	};
}
